/**
 * <freebird-back-in-stock> — the notify-me capture behind a sold-out variant.
 *
 * Ported from the flow proven in the Freebird Dawn theme, with three changes:
 * a native <dialog> instead of a hand-built overlay, an unchecked newsletter
 * box instead of a pre-ticked one, and behaviour delegated from this element
 * rather than bound to ids at load.
 *
 * WHY DELEGATION RATHER THAN DIRECT LISTENERS. This element lives inside a
 * `<freebird-variant-sync>` wrapper, whose contents are replaced wholesale when
 * the shopper changes variant. Listeners bound to inner nodes at load would be
 * lost on the first swap and, worse, the Dawn version's document-level id
 * lookups would keep resolving to nodes that had been detached. Delegating from
 * this element means the swap re-creates it, `connectedCallback` runs again, and
 * the bindings are correct by construction.
 *
 * THE KLAVIYO CALLS ARE UNAUTHENTICATED BY DESIGN. `a.klaviyo.com/client/*`
 * takes a public company id and is meant to be called from the browser; there is
 * no secret here to leak. It does mean the theme cannot rate-limit the endpoint —
 * that protection is Klaviyo's, and this has run in production on the Dawn theme
 * under the same terms.
 *
 * The company and list ids below are production Freebird's. A subscription
 * created from a staging theme lands in the live marketing account — verify with
 * a dedicated address and delete the profile afterwards.
 */

const KLAVIYO_COMPANY_ID = 'MaJKSj';
const KLAVIYO_REVISION = '2024-10-15';
const KLAVIYO_NEWSLETTER_LIST_ID = 'WhXRNR';
const KLAVIYO_BASE = 'https://a.klaviyo.com/client';

/** Klaviyo namespaces Shopify catalog variants with the integration that fed them. */
const CATALOG_VARIANT_PREFIX = '$custom:::$default:::';

class FreebirdBackInStock extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.handleClick);
    this.addEventListener('submit', this.handleSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('submit', this.handleSubmit);
  }

  /** @returns {HTMLDialogElement | null} */
  get dialog() {
    const id = this.dataset.fbBisModal;
    if (!id) return null;
    return /** @type {HTMLDialogElement | null} */ (this.querySelector(`#${CSS.escape(id)}`));
  }

  /** @param {Event} event */
  handleClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    if (target.closest('.fb-back-in-stock__trigger')) {
      event.preventDefault();
      this.dialog?.showModal();
      return;
    }

    if (target.closest('[data-fb-bis-close]')) {
      event.preventDefault();
      this.dialog?.close();
    }
  };

  /** @param {Event} event */
  handleSubmit = async (event) => {
    const form = /** @type {Element} */ (event.target).closest('[data-fb-bis-form]');
    if (!form) return;
    event.preventDefault();

    const emailInput = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-fb-bis-email]'));
    const optIn = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-fb-bis-optin]'));
    const submit = /** @type {HTMLButtonElement | null} */ (form.querySelector('.fb-bis__submit'));
    const status = /** @type {HTMLElement | null} */ (this.querySelector('[data-fb-bis-status]'));
    const variantId = this.dataset.fbBisVariantId;

    const email = emailInput?.value.trim();

    // Refused before any request goes out. `novalidate` is on the form so the
    // browser's own bubble does not fight the inline status message, which means
    // this check is the only one — it cannot be skipped.
    if (!email || !emailInput?.checkValidity() || !variantId) {
      this.#setStatus(status, 'Enter a valid email address.', false);
      emailInput?.focus();
      return;
    }

    if (!window.Freebird?.integrations?.klaviyo?.enabled) {
      this.#setStatus(status, 'Notifications are unavailable right now.', false);
      return;
    }

    const originalLabel = submit?.textContent;
    if (submit) {
      submit.disabled = true;
      submit.textContent = 'Submitting…';
    }
    if (status) status.hidden = true;

    try {
      const response = await fetch(`${KLAVIYO_BASE}/back-in-stock-subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email } } },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: CATALOG_VARIANT_PREFIX + variantId } },
            },
          },
        }),
      });

      // Klaviyo answers 202 on success, so `ok` alone would reject it.
      if (!response.ok && response.status !== 202) throw new Error(`Klaviyo responded ${response.status}`);

      // Best-effort and deliberately off the critical path: the shopper asked
      // to be told about the restock, and neither failing nor WAITING on a
      // marketing-list call is a trade worth making. Not awaited — awaiting it
      // would hold the confirmation below behind a request whose result is
      // discarded either way, leaving a disabled 'Submitting…' button on screen
      // for a subscription that already succeeded.
      if (optIn?.checked) {
        this.#subscribeToNewsletter(email).catch(() => {});
      }

      form.setAttribute('hidden', '');
      this.#setStatus(status, "You're in. We'll email you when it's back.", true);
    } catch {
      this.#setStatus(status, 'Something went wrong. Please try again.', false);
      if (submit) {
        submit.disabled = false;
        submit.textContent = originalLabel ?? 'Notify me';
      }
    }
  };

  /** @param {string} email */
  async #subscribeToNewsletter(email) {
    await fetch(`${KLAVIYO_BASE}/subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email } } },
            custom_source: 'Back in Stock Signup',
          },
          relationships: {
            list: { data: { type: 'list', id: KLAVIYO_NEWSLETTER_LIST_ID } },
          },
        },
      }),
    });
  }

  /**
   * @param {HTMLElement | null} status
   * @param {string} message
   * @param {boolean} success
   */
  #setStatus(status, message, success) {
    if (!status) return;
    status.textContent = message;
    status.hidden = false;
    status.classList.toggle('fb-bis__status--success', success);
    status.classList.toggle('fb-bis__status--error', !success);
  }
}

if (!customElements.get('freebird-back-in-stock')) {
  customElements.define('freebird-back-in-stock', FreebirdBackInStock);
}
